import type { ReactNode, WheelEvent } from 'react'

import { cn } from '@/lib/utils'

type ButtonProps = React.ButtonHTMLAttributes<HTMLButtonElement> & {
  tone?: 'primary' | 'secondary' | 'ghost' | 'danger'
}

export function Button({
  className,
  tone = 'primary',
  type = 'button',
  ...props
}: ButtonProps) {
  const toneClass =
    tone === 'primary'
      ? 'bg-accent-600 text-white hover:bg-accent-700'
      : tone === 'secondary'
        ? 'bg-ink-900 text-white hover:bg-ink-800'
        : tone === 'danger'
          ? 'bg-rose-600 text-white hover:bg-rose-700'
          : 'bg-white/70 text-ink-900 hover:bg-white'

  return (
    <button
      className={cn(
        'inline-flex items-center justify-center rounded-2xl px-4 py-2.5 text-sm font-semibold shadow-soft transition focus:outline-none focus:ring-2 focus:ring-accent-300 disabled:cursor-not-allowed disabled:opacity-60',
        toneClass,
        className,
      )}
      type={type}
      {...props}
    />
  )
}

type FieldProps = React.InputHTMLAttributes<HTMLInputElement>

function blurNumberInputOnWheel(event: WheelEvent<HTMLInputElement>) {
  if (event.currentTarget.type !== 'number') {
    return
  }

  event.currentTarget.blur()
}

export function Input({ className, onWheel, ...props }: FieldProps) {
  return (
    <input
      className={cn(
        'w-full rounded-2xl border border-ink-100 bg-white px-4 py-3 text-sm text-ink-900 shadow-sm outline-none transition placeholder:text-ink-400 focus:border-accent-300 focus:ring-2 focus:ring-accent-100',
        className,
      )}
      onWheel={(event) => {
        onWheel?.(event)
        if (!event.defaultPrevented) {
          blurNumberInputOnWheel(event)
        }
      }}
      {...props}
    />
  )
}

export function Textarea(props: React.TextareaHTMLAttributes<HTMLTextAreaElement>) {
  return (
    <textarea
      className={cn(
        'w-full rounded-2xl border border-ink-100 bg-white px-4 py-3 text-sm text-ink-900 shadow-sm outline-none transition placeholder:text-ink-400 focus:border-accent-300 focus:ring-2 focus:ring-accent-100',
        props.className,
      )}
      {...props}
    />
  )
}

export function Select(props: React.SelectHTMLAttributes<HTMLSelectElement>) {
  return (
    <select
      className={cn(
        'w-full rounded-2xl border border-ink-100 bg-white px-4 py-3 text-sm text-ink-900 shadow-sm outline-none transition focus:border-accent-300 focus:ring-2 focus:ring-accent-100',
        props.className,
      )}
      {...props}
    />
  )
}

export function Panel({
  children,
  className,
}: {
  children: ReactNode
  className?: string
}) {
  return (
    <section
      className={cn(
        'rounded-[28px] border border-white/70 bg-white/90 p-5 shadow-panel backdrop-blur',
        className,
      )}
    >
      {children}
    </section>
  )
}

export function PageHeading({
  eyebrow,
  title,
  description,
  actions,
}: {
  eyebrow?: string
  title: string
  description?: string
  actions?: ReactNode
}) {
  return (
    <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
      <div className="space-y-2">
        {eyebrow ? (
          <p className="text-xs font-semibold uppercase tracking-[0.24em] text-accent-700">
            {eyebrow}
          </p>
        ) : null}
        <h1 className="text-3xl font-bold tracking-tight text-ink-950">{title}</h1>
        {description ? <p className="max-w-3xl text-sm text-ink-500">{description}</p> : null}
      </div>
      {actions ? <div className="flex flex-wrap gap-3">{actions}</div> : null}
    </div>
  )
}

export function Badge({
  children,
  tone = 'neutral',
}: {
  children: ReactNode
  tone?: 'neutral' | 'success' | 'danger' | 'accent'
}) {
  const toneClass =
    tone === 'success'
      ? 'bg-emerald-50 text-emerald-700'
      : tone === 'danger'
        ? 'bg-rose-50 text-rose-700'
        : tone === 'accent'
          ? 'bg-accent-50 text-accent-700'
          : 'bg-ink-50 text-ink-600'

  return <span className={cn('rounded-full px-3 py-1 text-xs font-semibold', toneClass)}>{children}</span>
}

export function EmptyState({
  title,
  description,
  action,
}: {
  title: string
  description?: string
  action?: ReactNode
}) {
  return (
    <Panel className="flex min-h-56 flex-col items-center justify-center gap-3 text-center">
      <h3 className="text-xl font-semibold text-ink-950">{title}</h3>
      {description ? <p className="max-w-lg text-sm text-ink-500">{description}</p> : null}
      {action}
    </Panel>
  )
}

export function LoadingBlock({ label = 'Загрузка...' }: { label?: string }) {
  return (
    <Panel className="flex min-h-48 items-center justify-center">
      <div className="flex items-center gap-3 text-sm font-medium text-ink-500">
        <span className="h-3 w-3 animate-pulse rounded-full bg-accent-500" />
        {label}
      </div>
    </Panel>
  )
}

export function FloatingLoader({ label = 'Обновляем данные...' }: { label?: string }) {
  return (
    <div className="pointer-events-none fixed inset-0 z-50 flex items-center justify-center px-4">
      <div className="flex items-center gap-3 rounded-full border border-white/70 bg-white/92 px-5 py-3 shadow-panel backdrop-blur">
        <span className="h-2.5 w-2.5 animate-pulse rounded-full bg-accent-500" />
        <span className="text-sm font-semibold text-ink-700">{label}</span>
      </div>
    </div>
  )
}

export function FieldError({ message }: { message?: string }) {
  if (!message) {
    return null
  }

  return <p className="mt-2 text-xs font-medium text-rose-600">{message}</p>
}
